import React from "react";
import { Control, Controller, FieldErrors, UseFormSetValue } from "react-hook-form";
import { FormSection } from "./FormSection";
import { Checkbox, Combobox, Field, Input, InputGroup, RadioGroup, Textarea } from "@/components/ui";
import { LinkIcon, PhoneIcon, MailIcon } from "lucide-react";
import { FormSchemaType } from "../FormEditView";

export type CTATypeSuffix = "link" | "phone" | "email" | "share_player_link";
type CTATypeSuffixType = {
  key: CTATypeSuffix;
  label: string;
  icon: React.ReactNode;
  prefix: string;
  placeholder: string;
  validate: (value: string | null | undefined) => boolean | string;
};

const CTATypeSuffixes: Record<CTATypeSuffix, CTATypeSuffixType> = {
  link: {
    key: "link",
    label: "Link",
    icon: <LinkIcon className="size-4" />,
    prefix: "https://",
    placeholder: "example.com",
    validate: (value: string | null | undefined) => {
      return !/^(https:\/\/)?[\w-]+(\.[\w-]{2,})+([/?#][^\s]*)?$/i.test(value ?? "")
        ? "Please enter a valid URL (e.g. example.com)"
        : true;
    },
  },
  phone: {
    key: "phone",
    label: "Phone #",
    icon: <PhoneIcon className="size-4" />,
    prefix: "tel:",
    placeholder: "555-555-5555",
    validate: (value: string | null | undefined) => {
      return !/^(tel:)?\d{10}$/.test((value ?? "").replace(/[-()\s]/g, ""))
        ? "Please enter a valid 10-digit phone number"
        : true;
    },
  },
  email: {
    key: "email",
    label: "Email",
    icon: <MailIcon className="size-4" />,
    prefix: "mailto:",
    placeholder: "example@example.com",
    validate: (value: string | null | undefined) => {
      return !/^(mailto:)?[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(value ?? "") ? "Please enter a valid email address" : true;
    },
  },
  share_player_link: {
    key: "share_player_link",
    label: "Share Player Link",
    icon: <LinkIcon className="size-4" />,
    prefix: "",
    placeholder: "",
    validate: () => true,
  },
};

interface OutroSectionProps {
  control: Control<FormSchemaType>;
  errors: FieldErrors<FormSchemaType>;
  outroCtaLabel: string | null | undefined;
  outroCtaType: string | null | undefined;
  handleIncludeCtaCheckedChange: (checked: boolean) => void;
  setValue: UseFormSetValue<FormSchemaType>;
}

export function OutroSection({
  control,
  errors,
  outroCtaLabel,
  outroCtaType,
  handleIncludeCtaCheckedChange,
  setValue,
}: OutroSectionProps) {
  let ctaTypePrefix: "player" | "custom" = "player";
  let ctaTypeSuffix: CTATypeSuffix = "share_player_link";

  if (outroCtaType === "share_player_link") {
    ctaTypePrefix = "player";
    ctaTypeSuffix = "share_player_link";
  } else if (outroCtaType) {
    const [p, s] = outroCtaType.split("_");
    if (p === "player" || p === "custom") {
      ctaTypePrefix = p as any;
    }
    if (s === "link" || s === "phone" || s === "email") {
      ctaTypeSuffix = s as CTATypeSuffix;
    }
  }

  const handleSuffixChange = (newSuffix: CTATypeSuffix) => {
    if (newSuffix === "share_player_link") {
      setValue("outroCtaType", "share_player_link", { shouldDirty: true });
      setValue("outroCtaLink", null, { shouldDirty: true });
    } else {
      const prefix = ctaTypePrefix === "player" || ctaTypePrefix === "custom" ? ctaTypePrefix : "player";
      setValue("outroCtaType", `${prefix}_${newSuffix}`, { shouldDirty: true });
    }
  };

  const handlePrefixChange = (newPrefix: "player" | "custom") => {
    if (ctaTypeSuffix === "share_player_link") {
      setValue("outroCtaType", "share_player_link", { shouldDirty: true });
    } else {
      setValue("outroCtaType", `${newPrefix}_${ctaTypeSuffix}`, { shouldDirty: true });
      if (newPrefix === "player") {
        setValue("outroCtaLink", "", { shouldDirty: true });
      }
    }
  };

  return (
    <FormSection id="section-outro" title="Outro Section">
      <div className="flex flex-col gap-4">
        <Controller
          name="outroTitle"
          control={control}
          render={({ field, fieldState }) => (
            <Field label="Outro Title" name="outroTitle" error={fieldState.error?.message}>
              <Input
                {...field}
                value={field.value ?? ""}
                maxLength={255}
                placeholder="e.g. Thank you for your feedback!"
              />
            </Field>
          )}
        />
        <Controller
          name="outroText"
          control={control}
          render={({ field, fieldState }) => (
            <Field label="Outro Text" name="outroText" error={fieldState.error?.message}>
              <Textarea {...field} value={field.value ?? ""} placeholder="e.g. We appreciate your response." />
            </Field>
          )}
        />

        <div className="flex flex-col gap-2">
          <Field label="Call to Action" name="outroCta">
            <Checkbox
              label="Include a Call to Action button on the final Outro screen of this form?"
              checked={outroCtaLabel !== undefined && outroCtaLabel !== null}
              onCheckedChange={handleIncludeCtaCheckedChange}
            />
          </Field>

          {outroCtaLabel !== undefined && outroCtaLabel !== null && (
            <div className="ml-6 flex flex-col gap-4 max-w-xl">
              <Controller
                name="outroCtaLabel"
                control={control}
                render={({ field, fieldState }) => (
                  <Field label="Button Label" name="outroCtaLabel" error={fieldState.error?.message}>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      maxLength={50}
                      placeholder="Button Label"
                      className="w-full"
                    />
                  </Field>
                )}
              />

              <div className="flex flex-col gap-2">
                <Field label="Button Destination" name="outroCtaLink" error={errors.outroCtaLink?.message}>
                  <div className="flex flex-row items-center gap-4">
                    <Combobox
                      value={CTATypeSuffixes[ctaTypeSuffix]}
                      items={Object.values(CTATypeSuffixes)}
                      className="w-48"
                      onValueChange={val => handleSuffixChange(val?.key ?? ctaTypeSuffix)}
                    />
                    {ctaTypeSuffix !== "share_player_link" && (
                      <RadioGroup
                        value={ctaTypePrefix}
                        onValueChange={handlePrefixChange}
                        className="flex flex-row items-center gap-2 justify-start w-auto whitespace-nowrap"
                      >
                        <RadioGroup.Button
                          value="player"
                          id="form-cta-player"
                          label={`Player ${
                            ctaTypeSuffix === "link" ? "Short Link" : CTATypeSuffixes[ctaTypeSuffix].label
                          }`}
                        />
                        <RadioGroup.Button
                          value="custom"
                          id="form-cta-custom"
                          label={`Custom ${CTATypeSuffixes[ctaTypeSuffix].label}`}
                        />
                      </RadioGroup>
                    )}
                  </div>

                  {ctaTypeSuffix !== "share_player_link" && ctaTypePrefix === "custom" && (
                    <div className="pt-2">
                      <Controller
                        name="outroCtaLink"
                        control={control}
                        rules={{
                          validate: CTATypeSuffixes[ctaTypeSuffix].validate,
                        }}
                        render={({ field: linkField }) => (
                          <InputGroup
                            className="w-full"
                            prepend={
                              <p className="font-normal border-r border-input pr-2 text-muted-foreground text-sm">
                                {CTATypeSuffixes[ctaTypeSuffix].prefix}
                              </p>
                            }
                            control={controlProps => (
                              <Input
                                {...controlProps}
                                {...linkField}
                                value={linkField.value ?? ""}
                                maxLength={255}
                                placeholder={CTATypeSuffixes[ctaTypeSuffix].placeholder}
                                className="w-full grow"
                              />
                            )}
                          />
                        )}
                      />
                    </div>
                  )}
                </Field>
              </div>
            </div>
          )}
        </div>

        <Controller
          name="showSocialsOnOutro"
          control={control}
          render={({ field }) => (
            <Field label="Social Links" name="showSocialsOnOutro">
              <Checkbox
                label={`Show the Player's social links on the Outro screen?`}
                checked={!!field.value}
                onCheckedChange={field.onChange}
              />
            </Field>
          )}
        />
      </div>
    </FormSection>
  );
}
